import { Component } from "react"

/* Modal */
import Modal from "react-modal"

/* Redux */
import { connect } from "react-redux"

/* Translate */
import { t } from "../../translates/translate"

/* REST API */
import { detail } from "../../api/Log"

/* Helpers */
import { utils } from "../../helpers"

/* Icons */
import { CloseIcon, CopyIcon, InfoIcon, TableIcon, StudioIcon, ClockIcon, RepeatIcon, HashIcon, HistoryIcon, AlertIcon, WarningIcon } from "./LogIcons"

/* Utils */
import { getLevel, getName, formatDate, formatTime, formatDay, formatAgo } from "./LogUtils"

/* Sweet alert */
import Swal from "../../helpers/alert"


/* EMPTY MODAL STATE */
const EMPTY = {
    id: 0,
    status: "loading",
    log: null,
    repeats: null,
    around: [],
}


/* Widget Log Detail */
class LogDetail extends Component {

    constructor() {
        super()
        this.state = { visible: false, ...EMPTY }
    }

    /* Open Action */
    open = ({ id = 0 }) => {

        this.setState({ visible: true, ...EMPTY, id: parseInt(id) ? parseInt(id) : 0 }, () => {
            this.load()
        })
    }

    /* Close Action */
    close = () => {
        this.setState({ visible: false, ...EMPTY })
    }

    /* Load Action — the full message and the context of a record never travel with the list */
    load = () => {

        /* Fields */
        const { id } = this.state

        /* Set Status */
        this.setState({ status: "loading" })

        /* Send to REST API */
        detail(id).then((response) => {
            if (response.status === 200) {
                this.setState({
                    status: "ok",
                    log: response.data.log,
                    repeats: response.data.repeats,
                    around: response.data.around,
                })
            }
            else {
                this.setState({ status: "error" })
                Swal.fire(utils.notification(this.message(response), "error"))
                this.close()
            }
        }).catch(() => {
            this.setState({ status: "error" })
            Swal.fire(utils.notification(t("swal.generic"), "error"))
            this.close()
        })

    }

    /* Read The Error Message Of A Response */
    message = (response) => {

        if (response && response.data && response.data.message) {
            return response.data.message
        }

        if (response && response.status === 498) {
            return t("swal.invalidKey")
        }

        if (response && (response.status === 401 || response.status === 403)) {
            return t("swal.noAccess")
        }

        return t("swal.generic")
    }

    /* Copy Action — a stack trace goes to the clipboard, not to a screenshot */
    copy = (value) => {

        /* Check Value */
        if (!value) {
            return
        }

        /* Check Clipboard — an insecure origin has none */
        if (!navigator.clipboard) {
            Swal.fire(utils.notification(t("log.copyFailed"), "error"))
            return
        }

        navigator.clipboard.writeText(value).then(() => {
            Swal.fire(utils.notification(t("log.copied"), "success"))
        }).catch(() => {
            Swal.fire(utils.notification(t("log.copyFailed"), "error"))
        })

    }

    /* Draw Level Icon */
    _icon = (type, size) => {

        /* Fields */
        const level = getLevel(type)

        /* Check Level */
        if (level === "error") {
            return <AlertIcon size={size} />
        }

        if (level === "warning") {
            return <WarningIcon size={size} />
        }

        /* Send Result */
        return <InfoIcon size={size} />
    }

    /* Draw Message — the reason the section exists at all */
    _message = () => {

        /* Fields */
        const { log } = this.state

        return (
            <div className="log--section">

                <div className="log--section-head">

                    <div className="log--section-title">
                        {this._icon(log.type, 13)}
                        {t("log.message")}
                    </div>

                    <div onClick={() => this.copy(log.message)} className="log--section-action">
                        <CopyIcon size={13} />
                        <span>{t("log.copy")}</span>
                    </div>

                </div>

                <pre className={`log--message ${getLevel(log.type)}`}>{utils.checkValue(log.message, t("log.noMessage"))}</pre>

            </div>
        )
    }

    /* Draw Information — where the record came from and when */
    _information = () => {

        /* Fields */
        const { log } = this.state

        return (
            <div className="log--section">

                <div className="log--section-head">
                    <div className="log--section-title">
                        <InfoIcon size={13} />
                        {t("log.information")}
                    </div>
                </div>

                <div className="log--info">

                    <div className="log--info-item">
                        <TableIcon />
                        <div className="log--info-text">
                            <div className="log--info-title">{t("log.game")}</div>
                            <div className="log--info-value">{getName(log)}</div>
                        </div>
                    </div>

                    <div className="log--info-item">
                        <StudioIcon />
                        <div className="log--info-text">
                            <div className="log--info-title">{t("log.studio")}</div>
                            <div className="log--info-value">{utils.checkValue(log.studioName, t("log.noStudio"))}</div>
                        </div>
                    </div>

                    <div className="log--info-item">
                        <HashIcon />
                        <div className="log--info-text">
                            <div className="log--info-title">{t("log.table")}</div>
                            <div className="log--info-value">{utils.checkValue(log.table, "-")}</div>
                        </div>
                    </div>

                    <div className="log--info-item">
                        <ClockIcon />
                        <div className="log--info-text">
                            <div className="log--info-title">{t("log.date")}</div>
                            <div className="log--info-value">{formatDate(log.createdAt)}</div>
                        </div>
                    </div>

                </div>

                {/* The slug is gone from Tables — the game was renamed or deleted, the records stayed */}
                {!log.tableName && log.table &&
                    <div className="log--hint">
                        <InfoIcon />
                        <span>{t("log.tableMissingHint")}</span>
                    </div>
                }

            </div>
        )
    }

    /* Draw Repeats — one accident and a permanent failure look the same until they are counted */
    _repeats = () => {

        /* Fields */
        const { repeats } = this.state
        const count = repeats ? parseInt(repeats.count) : 1

        return (
            <div className="log--section">

                <div className="log--section-head">
                    <div className="log--section-title">
                        <RepeatIcon />
                        {t("log.repeats")}
                    </div>
                </div>

                <div className={`log--repeats ${count > 1 ? "many" : ""}`}>

                    <div className="log--repeats-count">{count}</div>

                    <div className="log--repeats-text">
                        <div className="log--repeats-name">{count > 1 ? t("log.repeatsMany") : t("log.repeatsOnce")}</div>
                        <div className="log--repeats-note">
                            {count > 1
                                ? `${t("log.repeatsFirst")} ${formatDate(repeats.first)} · ${t("log.repeatsLast")} ${formatDate(repeats.last)}`
                                : t("log.repeatsOnceNote")
                            }
                        </div>
                    </div>

                </div>

            </div>
        )
    }

    /* Draw Around — the neighbours of the record inside the same table, a cause usually stands next to it */
    _around = () => {

        /* Fields */
        const { around, log } = this.state

        return (
            <div className="log--section">

                <div className="log--section-head">
                    <div className="log--section-title">
                        <HistoryIcon />
                        {t("log.around")}
                        <span className={`log--counter ${around.length === 0 ? "empty" : ""}`}>{around.length}</span>
                    </div>
                </div>

                {around.length === 0 &&
                    <div className="log--chips-empty">{t("log.aroundEmpty")}</div>
                }

                {around.map((item, index) =>
                    <div onClick={() => this.open({ id: item.id })} className="log--near" key={`${index}`}>

                        <div className={`log--near-level ${getLevel(item.type)}`}>{this._icon(item.type, 13)}</div>

                        <div className="log--near-text">
                            <div className="log--near-message">{utils.checkValue(item.message, t("log.noMessage"))}</div>
                            <div className="log--near-note">
                                <span>#{item.id}</span>
                                <span>{formatDay(item.createdAt)} {formatTime(item.createdAt)}</span>
                                <span>{item.id > log.id ? t("log.after") : t("log.before")}</span>
                            </div>
                        </div>

                    </div>
                )}

            </div>
        )
    }

    /* Draw Body */
    _body = () => {

        /* Fields */
        const { status } = this.state

        /* Check Status */
        if (status !== "ok") {
            return <div className="log--modal-loading"><div className="log--loading" /></div>
        }

        return (
            <div className="log--modal-body">

                {/* Draw Message */}
                {this._message()}

                {/* Draw Information */}
                {this._information()}

                {/* Draw Repeats */}
                {this._repeats()}

                {/* Draw Around */}
                {this._around()}

            </div>
        )
    }

    /* Draw Footer */
    _footer = () => {

        /* Fields */
        const { status, log } = this.state

        /* Check Status */
        if (status !== "ok") {
            return null
        }

        return (
            <div className="log--modal-footer space">

                <div onClick={() => this.copy(log.message)} className="log--button primary">
                    <CopyIcon />
                    <span>{t("log.copyMessage")}</span>
                </div>

                <div onClick={() => this.close()} className="log--button ghost">{t("common.close")}</div>

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { visible, log, id } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={() => this.close()}
                className="log--modal"
                overlayClassName="log--overlay"
                closeTimeoutMS={200}
            >

                <div className="log--modal-header">

                    <div className={`log--modal-icon ${log ? getLevel(log.type) : ""}`}>
                        {this._icon(log ? log.type : "", 18)}
                    </div>

                    <div className="log--modal-head-text">
                        <h2>{log ? getName(log) : t("log.detailTitle")}</h2>
                        <p>
                            {log
                                ? `#${log.id} · ${String(log.type).toUpperCase()} · ${formatAgo(log.createdAt)}`
                                : `#${id}`
                            }
                        </p>
                    </div>

                    <div onClick={() => this.close()} className="log--modal-close">
                        <CloseIcon />
                    </div>

                </div>

                {/* Draw Body */}
                {this._body()}

                {/* Draw Footer */}
                {this._footer()}

            </Modal>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(LogDetail)
